import { FC, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { createIdentificationStatusQueryOptions } from "@/hooks/identificationStatusQueryOptions";
import { KYC_STATUS_ONBOARDING_NOT_FINISHED } from "@/hooks/useNeedsIDCheck";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useGetIdentificationStatus,
  useGetIdentificationURL,
} from "@/services/api";
import {
  IdentificationStatusResponse,
  IdentificationStatusResponseKycStatus,
} from "@/services/model";

import { WarningHeader } from "./WarningHeader";
import { WarningWithKYCButton } from "./WarningWithKYCButton";

type KYCWarningProps = {
  /** Show prep checklist + ID Check link inline instead of behind a modal. */
  inlinePrep?: boolean;
  /** Render content for the dedicated ID check page task card. */
  pageLayout?: boolean;
};

export const KYCWarning: FC<KYCWarningProps> = ({
  inlinePrep = false,
  pageLayout = false,
}) => {
  const { t, i18n } = useTranslation();

  const staleTime = useRef(0);
  const {
    data: identificationStatus,
  }: {
    // This type construction can be removed once KYC_invalid_document was added to the BE
    // https://digitalrepublicag.atlassian.net/browse/DRDEV-875
    data:
      | IdentificationStatusResponse
      | undefined
      | {
          isVerified?: boolean;
          kycStatus?:
            | "KYC_invalid_document"
            | "KYC_max_attempts"
            | "KYC_ID_uncomparable"
            | typeof KYC_STATUS_ONBOARDING_NOT_FINISHED;
        };
  } = useGetIdentificationStatus({
    query: createIdentificationStatusQueryOptions(staleTime),
  });

  const { data: identificationUrl } = useGetIdentificationURL({
    query: {
      enabled:
        identificationStatus?.kycStatus ===
          IdentificationStatusResponseKycStatus.OPEN ||
        identificationStatus?.kycStatus === "KYC_invalid_document" ||
        identificationStatus?.kycStatus === "KYC_ID_uncomparable" ||
        identificationStatus?.kycStatus ===
          IdentificationStatusResponseKycStatus.KYC_error_processing_selfie ||
        identificationStatus?.kycStatus ===
          IdentificationStatusResponseKycStatus.KYC_error_processing_docs,
      refetchOnWindowFocus: true,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  });

  const identificationUrlWithLanguage = useMemo(() => {
    if (identificationUrl) {
      const url = new URL(identificationUrl);
      url.searchParams.set("lang", i18n?.resolvedLanguage ?? "en");

      return url.toString();
    }
  }, [identificationUrl, i18n.resolvedLanguage]);

  if (!identificationStatus) {
    return null;
  }

  // Onboarding incomplete — KYC is not actionable yet; hide the todo entirely.
  if (
    (identificationStatus.kycStatus as string | undefined) ===
    KYC_STATUS_ONBOARDING_NOT_FINISHED
  ) {
    return null;
  }

  if (
    identificationStatus?.kycStatus ===
    IdentificationStatusResponseKycStatus.KYC_error_processing_selfie
  ) {
    return (
      <WarningWithKYCButton
        title={t("cockpit.kycSection.title.KYC_error_processing_selfie")}
        info={t("cockpit.kycSection.errors.KYC_error_processing_selfie")}
        buttonLabel={
          pageLayout
            ? t("cockpit.idCheck.button")
            : t("cockpit.id-banner.open.button")
        }
        target={identificationUrlWithLanguage}
        inlinePrep={inlinePrep}
        pageLayout={pageLayout}
      />
    );
  } else if (
    identificationStatus?.kycStatus ===
    IdentificationStatusResponseKycStatus.KYC_error_processing_docs
  ) {
    return (
      <WarningWithKYCButton
        title={t("cockpit.kycSection.title.KYC_error_processing_docs")}
        info={t("cockpit.kycSection.errors.KYC_error_processing_docs")}
        buttonLabel={
          pageLayout
            ? t("cockpit.idCheck.button")
            : t("cockpit.id-banner.open.button")
        }
        target={identificationUrlWithLanguage}
        inlinePrep={inlinePrep}
        pageLayout={pageLayout}
      />
    );
  } else if (
    identificationStatus?.kycStatus ===
    IdentificationStatusResponseKycStatus.FRAUD_CHECK_PENDING
  ) {
    return (
      <WarningHeader
        title={t("cockpit.id-banner.fraudCheck.title")}
        info={t("cockpit.id-banner.fraudCheck.info")}
        icon={null}
        button={null}
      />
    );
  } else if (
    identificationStatus?.kycStatus ===
    IdentificationStatusResponseKycStatus.PENDING
  ) {
    return (
      <WarningWithKYCButton
        title={t("cockpit.id-banner.pending.title")}
        info={t("cockpit.id-banner.pending.info")}
        pageLayout={pageLayout}
      />
    );
  } else if (
    identificationStatus?.kycStatus ===
    IdentificationStatusResponseKycStatus.KYC_data_mismatch
  ) {
    return (
      <WarningWithKYCButton
        title={t("cockpit.kycSection.title.personalDetails")}
        info={t("cockpit.kycSection.errors.personalDetails")}
        buttonLabel={t("cockpit.kycSection.buttonLabel.personalDetails")}
        target={portalRoutes.user.personal}
        type="internal"
        pageLayout={pageLayout}
      >
        <div className="mt-4">
          <ul className="list-inside list-disc">
            <li>{t("cockpit.kycSection.toCorrect.names")}</li>
            <li>{t("cockpit.kycSection.toCorrect.dateOfBirth")}</li>
          </ul>
        </div>
      </WarningWithKYCButton>
    );
  } else if (
    identificationStatus?.kycStatus ===
    IdentificationStatusResponseKycStatus.KYC_underaged
  ) {
    return (
      <WarningWithKYCButton
        title={t("cockpit.kycSection.title.underaged")}
        info={t("cockpit.kycSection.errors.underaged")}
        buttonLabel={t("cockpit.kycSection.buttonLabel.underaged")}
        target={t("cockpit.kycSection.links.underaged")}
        pageLayout={pageLayout}
      />
    );
  } else if (identificationStatus?.kycStatus === "KYC_invalid_document") {
    return (
      <WarningWithKYCButton
        title={t("cockpit.kycSection.title.invalidDocument")}
        info={t("cockpit.kycSection.errors.invalidDocument")}
        buttonLabel={
          pageLayout
            ? t("cockpit.idCheck.button")
            : t("cockpit.id-banner.open.button")
        }
        target={identificationUrlWithLanguage}
        inlinePrep={inlinePrep}
        pageLayout={pageLayout}
      />
    );
  } else if (
    (identificationStatus?.kycStatus as string | undefined) ===
    "KYC_max_attempts"
  ) {
    return (
      <WarningWithKYCButton
        title={t("cockpit.kycSection.title.maxAttempts")}
        info={t("cockpit.kycSection.errors.maxAttempts")}
        buttonLabel={t("cockpit.kycSection.buttonLabel.maxAttempts")}
        target={t("cockpit.kycSection.links.maxAttempts")}
        pageLayout={pageLayout}
      />
    );
  } else if (identificationStatus?.kycStatus === "KYC_ID_uncomparable") {
    return (
      <WarningWithKYCButton
        title={t("cockpit.kycSection.title.IDUncomparable")}
        info={t("cockpit.kycSection.errors.IDUncomparable")}
        buttonLabel={
          pageLayout
            ? t("cockpit.idCheck.button")
            : t("cockpit.id-banner.open.button")
        }
        target={identificationUrlWithLanguage}
        inlinePrep={inlinePrep}
        pageLayout={pageLayout}
      />
    );
  } else {
    return (
      <WarningWithKYCButton
        title={
          pageLayout
            ? t("cockpit.idCheck.title")
            : t("cockpit.id-banner.open.title")
        }
        info={
          pageLayout
            ? t("cockpit.idCheck.info")
            : t("cockpit.id-banner.open.info")
        }
        warningInfo={
          pageLayout
            ? t("cockpit.idCheck.internationalCallsWarning")
            : undefined
        }
        buttonLabel={
          pageLayout
            ? t("cockpit.idCheck.button")
            : t("cockpit.id-banner.open.button")
        }
        target={identificationUrlWithLanguage}
        inlinePrep={inlinePrep}
        pageLayout={pageLayout}
      />
    );
  }
};
